import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services';
import type {
  AuthResponse,
  ChangePasswordData,
  LoginCredentials,
  RegisterData,
  User,
} from '@/types';
import { extractErrorMessage } from '@/utils';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(credentials);
          localStorage.setItem('accessToken', response.accessToken);
          set({
            accessToken: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          await get().fetchCurrentUser();
          return response;
        } catch (error) {
          const message = extractErrorMessage(error, 'Login failed');
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          await authService.register(data);
          set({ isLoading: false });
        } catch (error) {
          const message = extractErrorMessage(error, 'Registration failed');
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {}
        localStorage.removeItem('accessToken');
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        });
      },

      changePassword: async (data) => {
        set({ isLoading: true, error: null });
        try {
          await authService.changePassword(data);
          await get().fetchCurrentUser();
          set({ isLoading: false });
        } catch (error) {
          const message = extractErrorMessage(error, 'Password change failed');
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      fetchCurrentUser: async () => {
        try {
          const user = await authService.getCurrentUser();
          set({ user, isAuthenticated: true });
        } catch (error) {
          set({ user: null, isAuthenticated: false });
          throw error;
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export default useAuthStore;
